import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { TimePeriod } from '../../../design/selects/timePeriodSelector/S_TimePeriodSelector';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError } from '../../models/error.model';
import { currentTimeZone } from '../../models/timeZones/timeZones.model';
import { IMainApiService, IPagedResult } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { ICryptoService } from '../../services/crypto.service';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { ITeamMember } from '../team/models/TeamMember';
import { UserInfoLoadedEventType } from '../user/events/userInfoLoadedEvent';
import { IUserService } from '../user/user.service';
import { WorkspaceDeletedEvent } from './events/workspaceDeletedEvent';
import { WorkspaceTimeZoneUpdatedEvent, WorkspaceTimeZoneUpdatedEventType } from './sub/UpdateWorkspaceTimeZone/events/WorkspaceTimeZoneUpdatedEvent';
import { ITakeOverWorkspaceLimits, IWorkspace, IWorkspaceListItem, IWorkspacesStore, WorkspaceRole } from './workspaces.store';

export interface IWorkspaceService {
  getUserRoleInCurrentWorkspace(): WorkspaceRole;
  isInRole(roles: WorkspaceRole[]): boolean;
  changeOwnerAsync(userEmail: string, workspaceId?: string): Promise<void | ACDLError>;
  getCurrentWorkspaceMembersAsync(): Promise<void | ACDLError>;
  getTakeOverWorkspaceLimitsAsync(userEmail: string): Promise<ITakeOverWorkspaceLimits | ACDLError>;
  initWorkspaceListAsync(): Promise<void>;
  disposeWorkspaceListAsync(): void;
  getWorkspaceListItemsAsync(): Promise<void | ACDLError>;
  createWorkspaceAsync(name: string, encrypted: boolean, timeZoneOffset: number): Promise<string | ACDLError>;
  renameWorkspaceAsync(workspaceId: string, name: string): Promise<void | ACDLError>;
  deleteWorkspaceAsync(id: string): Promise<void | ACDLError>;
  deleteCurrentWorkspaceAsync(): Promise<void | ACDLError>;
  resetCurrentWorkspaceAsync(): Promise<void | ACDLError>;
  setPreferredWorkspace(workspaceId: string): void;
  resetWorkspaceAsync(workspaceId: string): Promise<void>;
  resetUserWorkspacesAsync(userId: string): Promise<void>;
  resetUserWorkspaceAsync(workspaceId: string): Promise<void>;
  store: IWorkspacesStore;
}

export namespace IWorkspaceService {
  export const $: interfaces.ServiceIdentifier<IWorkspaceService> = Symbol('WORKSPACES_SERVICE');
}

const apiUrl = '/Workspace/';

@injectable()
export class WorkspaceService implements IWorkspaceService {
  disposers: EventListeningDisposer[] = [];

  constructor(
    @inject(IWorkspacesStore.$) public readonly store: IWorkspacesStore,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(ICryptoService.$) private readonly cryptoService: ICryptoService,
    @inject(IUserService.$) private readonly userService: IUserService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
    eventBus.addListener<WorkspaceTimeZoneUpdatedEvent>(this.workspaceTimeZoneUpdatedListenerAsync, WorkspaceTimeZoneUpdatedEventType);
  }

  @action
  workspaceTimeZoneUpdatedListenerAsync = async (event: WorkspaceTimeZoneUpdatedEvent): Promise<void> => {
    const workspace = this.store.workspaces.find((w) => w.id === event.workspaceId);
    if (workspace) {
      workspace.timeZoneOffset = event.timezone;
    }

    await this.getWorkspaceListItemsAsync();
  };

  setPreferredWorkspace(workspaceId: string): void {
    this.store.setPreferredWorkspace(workspaceId);
  }

  isInRole(workspaceRoles: WorkspaceRole[]): boolean {
    const userRoleInWorkspace = this.getUserRoleInCurrentWorkspace();
    return workspaceRoles.includes(userRoleInWorkspace);
  }

  @action.bound
  async initWorkspaceListAsync(): Promise<void> {
    this.disposers = [this.eventBus.addListener(this.userInfoLoadedListener, UserInfoLoadedEventType)];
    await this.getWorkspaceListItemsAsync();
  }

  @action.bound
  disposeWorkspaceListAsync(): void {
    this.disposers.forEach((d) => {
      d();
    });
    this.disposers = [];
  }

  @action.bound
  async createWorkspaceAsync(name: string, encrypted: boolean, timeZoneOffset: number): Promise<string | ACDLError> {
    const url = `${apiUrl}Create`;
    const result = await this.apiService.postAsync<string>(url, {
      name,
      encrypted,
      timeZoneOffset,
    });

    if (result instanceof ACDLError) {
      return result;
    }

    const workspaceId = result;
    let checksum = undefined;
    if (encrypted) {
      checksum = await this.cryptoService.generateKey(workspaceId, workspaceId);
      const result = await this.apiService.postAsync<string>(`${apiUrl}EnableEncryption`, { workspaceId, checksum });
      if (result instanceof ACDLError) {
        return result;
      }
    }

    this.store.addWorkspace(workspaceId, name.trim(), this.userService.data.planInfo.plan, timeZoneOffset, '', checksum);
    await this.getWorkspaceListItemsAsync();

    return workspaceId;
  }

  @action.bound
  async renameWorkspaceAsync(workspaceId: string, name: string): Promise<ACDLError | undefined> {
    const url = `${apiUrl}Rename`;
    const result = await this.apiService.postAsync<string>(url, { name, workspaceId });

    if (result instanceof ACDLError) {
      return result;
    }

    const newWorkspaces = this.store.workspaces.slice();
    const workspace = newWorkspaces.find((w) => w.id === workspaceId);
    if (workspace) {
      workspace.name = name.trim();
    }
    this.store.workspaces.replace(newWorkspaces);

    const index = this.store.workspaceListItems.findIndex((w) => w.id === workspaceId);
    if (index === -1) {
      return Promise.resolve(undefined);
    }
    const newListItems = this.store.workspaceListItems.slice();
    newListItems[index].name = name.trim();
    this.store.workspaceListItems.replace(newListItems);

    return Promise.resolve(undefined);
  }

  @action.bound
  async deleteWorkspaceAsync(workspaceId: string): Promise<void> {
    await this.performWorkspaceDeletionAsync(workspaceId);
  }

  @action.bound
  async deleteCurrentWorkspaceAsync(): Promise<void> {
    const workspace = this.currentWorkspaceStore.currentWorkspace;
    if (!workspace) {
      return;
    }

    await this.performWorkspaceDeletionAsync(workspace.id);
  }

  @action.bound
  async resetCurrentWorkspaceAsync(): Promise<void> {
    const workspace = this.currentWorkspaceStore.currentWorkspace;
    if (!workspace) {
      return;
    }

    await this.resetWorkspaceAsync(workspace.id);

    window.location.reload();
  }

  @action.bound
  async resetWorkspaceAsync(workspaceId: string): Promise<void> {
    const resetResult = await this.apiService.postAsync<string>(`${apiUrl}Reset`, {
      workspaceId,
    });

    if (resetResult instanceof ACDLError) {
      throw new ACDLError('Something went wrong during workspace reset');
    }
  }

  @action.bound
  async resetUserWorkspacesAsync(userId: string): Promise<void> {
    const resetResult = await this.apiService.postAsync<string>(`${apiUrl}resetUserWorkspaces`, {
      userId,
    });

    if (resetResult instanceof ACDLError) {
      throw new ACDLError('Something went wrong during workspace reset');
    }
  }

  @action.bound
  async resetUserWorkspaceAsync(workspaceId: string): Promise<void> {
    const resetResult = await this.apiService.postAsync<string>(`${apiUrl}resetUserWorkspace`, {
      workspaceId,
    });

    if (resetResult instanceof ACDLError) {
      throw new ACDLError('Something went wrong during workspace reset');
    }
  }

  private async performWorkspaceDeletionAsync(workspaceId: string): Promise<void> {
    const currentTimeZoneOffset = currentTimeZone?.value || 0;
    const deleteResult = await this.apiService.postAsync<string>(`${apiUrl}Delete`, {
      id: workspaceId,
      timeZoneOffset: currentTimeZoneOffset,
    });
    if (deleteResult instanceof ACDLError) {
      return;
    }

    const getResult = await this.apiService.getAsync<IWorkspace[]>(`${apiUrl}All`, {});
    if (getResult instanceof ACDLError) {
      return;
    }

    if (getResult.length === 0) {
      throw new ACDLError('No user workspaces');
    }

    this.store.workspaces.replace(getResult);

    this.eventBus.sendEvent(new WorkspaceDeletedEvent());

    this.store.workspaceListItems.replace(this.store.workspaceListItems.filter((w) => w.id !== workspaceId));
    if (this.store.workspaceListItems.length === 0) {
      await this.getWorkspaceListItemsAsync();
    }
  }

  getUserRoleInCurrentWorkspace(): WorkspaceRole {
    const workspace = this.store.workspaces.find((x) => x.id === this.currentWorkspaceStore.currentWorkspace.id);
    return workspace ? workspace.role : WorkspaceRole.None;
  }

  @action.bound
  async changeOwnerAsync(userEmail: string, workspaceId: string = ''): Promise<ACDLError | undefined> {
    const result = await this.apiService.postAsync<{ userEmail: string; workspaceId: string }>(`${apiUrl}changeWorkspaceOwner`, {
      userEmail,
      workspaceId: workspaceId !== '' ? workspaceId : this.currentWorkspaceStore.currentWorkspace.id,
    });

    if (result instanceof ACDLError) {
      return result;
    }

    const newWorkspaces = this.store.workspaces.slice();
    const workspace = newWorkspaces.find((w) => w.id === this.currentWorkspaceStore.currentWorkspace.id);
    if (workspace) {
      workspace.role = WorkspaceRole.Manager;
    }
    this.store.workspaces.replace(newWorkspaces);

    await this.getCurrentWorkspaceMembersAsync();
    await this.getWorkspaceListItemsAsync();

    return Promise.resolve(undefined);
  }

  @action.bound
  async getCurrentWorkspaceMembersAsync(): Promise<void> {
    const result = await this.apiService.getAsync<IPagedResult<ITeamMember>>(`${apiUrl}getTeamMembers`, {
      params: { workspaceId: this.currentWorkspaceStore.currentWorkspace.id, timePeriod: TimePeriod.All, pageNumber: 1, pageSize: Number.MAX_SAFE_INTEGER },
    });

    if (result instanceof ACDLError) {
      return;
    }

    this.store.setWorkspaceMembers(result.data);
  }

  @action.bound
  async getTakeOverWorkspaceLimitsAsync(userEmail: string): Promise<ACDLError | ITakeOverWorkspaceLimits> {
    const result = await this.apiService.getAsync<ITakeOverWorkspaceLimits>(
      `${apiUrl}getTakeOverWorkspaceLimits?workspaceId=${this.currentWorkspaceStore.currentWorkspace.id}&email=${userEmail}`
    );

    if (!(result instanceof ACDLError)) {
      this.store.takeOverWorkspaceLimits = result;
    }

    return result;
  }

  @action.bound
  async getWorkspaceListItemsAsync(): Promise<void | ACDLError> {
    this.store.showLoader = true;
    const result = await this.apiService.getAsync<IPagedResult<IWorkspaceListItem>>(`${apiUrl}getList`, {
      params: {
        workspaceName: this.store.workspaceNameSearch,
        owner: this.store.workspaceOwnerSearch,
        pageSize: this.store.workspacesPaging.pageSize,
        pageNumber: this.store.workspacesPaging.pageNumber,
        orderBy: this.store.workspacesPaging.orderBy,
        orderType: this.store.workspacesPaging.orderType,
      },
    });

    if (result instanceof ACDLError) {
      this.store.showLoader = false;
      return;
    }

    if (result.totalCount > 0 && result.pagesCount < result.pageNumber) {
      this.store.workspacesPaging.pageNumber = result.pagesCount;
      await this.getWorkspaceListItemsAsync();
    } else {
      this.store.workspacesPaging = {
        ...this.store.workspacesPaging,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
      this.store.workspaceListItems.replace(result.data);
    }

    this.store.showLoader = false;
  }

  private userInfoLoadedListener = async (): Promise<void | ACDLError> => this.getWorkspaceListItemsAsync();
}
